import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Layers, Split } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

/** The treatments a line can carry, as the dashboard labels them. */
const TREATMENTS: { name: string; body: string }[] = [
  {
    name: 'Standard',
    body: 'A rate above 0%. You publish it as a rate with this treatment.',
  },
  {
    name: 'Reduced',
    body: 'A rate above 0% for reduced-rate supplies. Scope it to selected tax categories and it replaces the standard rate for those items instead of adding to it.',
  },
  {
    name: 'Zero-rated',
    body: 'A rate of exactly 0%. The supply is taxable at nothing, and its taxable base is reported separately from the tax collected.',
  },
  {
    name: 'Exempt',
    body: 'No tax. Used for a rate of 0% marked exempt, and for every line of a client with a valid exemption. Prints your exempt legend.',
  },
  {
    name: 'Reverse charge',
    body: 'No tax. Decided by Revvly, never stored as a rate: the client accounts for the tax. Prints your reverse-charge legend.',
  },
  {
    name: 'Out of scope',
    body: 'No tax. Decided by Revvly when tax is off, no registration is in force, you hold no registration covering the client, or no published rate matches.',
  },
];

export function TaxGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Collect tax on invoices — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Collect tax</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Collect tax on invoices, <em>from your own rates</em>.
        </h1>
        <p className="dp-oneliner">
          Register where you are liable, publish the rates you charge, describe each client&rsquo;s
          tax position, and Revvly adds tax to every invoice when it is issued. Every taxed line is
          also written to a ledger that the tax report reads.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Who can use it
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Flag</div>
            <div className="dp-field-v">
              Tax is a workspace feature (<span className="api-inline">TAX</span>) granted by
              Revvly. Without it there is no Tax tab in Settings, no Tax tab on a client and no Tax
              tab in Finance. Ask Revvly if you expect it and cannot see it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who can change things</div>
            <div className="dp-field-v">
              Workspace admins. Other members see the Tax pages read-only, and the API refuses their
              writes. Report exports and the ledger check are admin only as well.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Pricing basis</div>
            <div className="dp-field-v">
              Tax-exclusive only. Catalog prices are net and tax is added on top of every invoice
              line. Tax-inclusive pricing is shown in Settings as not available yet.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Add a registration, then switch tax on
        </h2>
        <p className="dp-plain">
          A registration is your own tax number in a country or subdivision. Every rate belongs to
          one, and tax cannot be switched on until at least one active registration exists.
        </p>
        <UiPath
          steps={['Settings', 'Tax', 'Settings & registrations', 'Add registration']}
          note="Fields: Country, Scheme (VAT, GST, Sales tax, Consumption tax or Other), Subdivision (optional), Registration number, Legal name (if different), Effective from, Effective to (optional)."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Use ISO codes: a two-letter country and, for a subdivision, a code inside that country such
          as <span className="api-inline">CA-QC</span>. One registration is primary; its country is
          the one assumed for a client that has no tax profile. Ending a registration sets an end
          date and keeps the record, and the rates attached to it stop applying from that date.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Then turn on <strong>Charge tax on invoices</strong> under Tax collection. Turning it off
          later leaves issued invoices and reports intact.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Reverse charge</div>
            <div className="dp-field-v">
              Off by default. When on, a business client with a tax ID in its own country, flagged
              eligible, and in a country where you hold no registration, is invoiced without tax.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Show tax IDs on invoices</div>
            <div className="dp-field-v">
              On by default. Prints your registration number and the buyer&rsquo;s tax ID.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Legends</div>
            <div className="dp-field-v">
              The Reverse-charge legend and the Exempt legend, each up to 500 characters, printed on
              an invoice that has a line of that treatment.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Reporting basis</div>
            <div className="dp-field-v">
              Shown as Accrual, with Cash marked not available yet. The tax report itself can still
              be run on either basis (see section 08).
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Publish rates
        </h2>
        <UiPath
          steps={['Settings', 'Tax', 'Rates', 'New rate']}
          note="Save a draft, preview it against a client, then Publish."
        />
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Identity</div>
            <div className="dp-field-v">
              Name, Code (shared by every version of the rate), Tax code (the levy, such as VAT or
              QST) and Invoice label (printed on the invoice, for example VAT 23%).
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Rate</div>
            <div className="dp-field-v">
              Tax type, Treatment and Rate (%), from 0 to 100 with up to four decimals. Zero-rated
              and Exempt must be 0%; Standard and Reduced must be above 0%.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Where</div>
            <div className="dp-field-v">
              Country, Subdivision (optional) and the Registration it sits under. The registration
              must be active when you publish.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Effective dates</div>
            <div className="dp-field-v">
              From, and To (optional). An invoice uses the rates in force on its invoice date.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Stacking</div>
            <div className="dp-field-v">
              Sequence (1 to 99) orders the rates on one line. Compound applies the rate to the base
              plus the taxes with a lower sequence; without it the rate is additive, on the same
              base as the others. Replaces country rates (subdivision rates only) drops the
              country-level rates for clients in that subdivision, as with Canadian HST.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Applies to</div>
            <div className="dp-field-v">
              All items, or Selected tax categories (a comma-separated list). A rate scoped to a
              line&rsquo;s category beats an all-items rate for the same levy, which is how a
              reduced rate sits beside the standard one.
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Only published rates are ever used. A published rate is not edited. Choose{' '}
          <strong>New version</strong> to copy it into a draft, set a later start date and publish:
          the old version is closed at the new start, so earlier invoices keep the old rate. Publish
          is refused when another published rate of the same levy, place and items overlaps in time.{' '}
          <strong>Archive</strong> retires a rate without deleting it.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Start from the reference rates
        </h2>
        <UiPath steps={['Settings', 'Tax', 'Reference data']} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Revvly maintains an indicative list of rates by country, with the date each was last
          checked. Tick the layers you want and choose <strong>Import</strong> (the button reads Import N as drafts). Imports
          are drafts that start today, attached to the registration that covers them, and nothing is
          live until you publish. A layer is refused if it is already imported, if you have a rate
          with the same code that did not come from this list, or if no registration of the right
          scheme covers it. Revvly never changes a rate you own: when the reference data moves on,
          the page lists the imported rates that now differ so you can publish a new version.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Describe each client&rsquo;s tax position
        </h2>
        <UiPath
          steps={['Clients', 'open a client', 'Tax']}
          note="Not shown on a Company that is not yet a client."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The profile holds the billing Country and Subdivision, Business or Consumer, any number of
          Tax IDs (country, type and number, one per country), an <strong>Exempt from tax</strong>{' '}
          switch with Reason, Certificate reference, Valid from and Valid to, and{' '}
          <strong>Eligible for reverse charge</strong>. Tax IDs are stored as entered and not
          checked against an external service. An exemption is ignored outside its dates, and the
          Valid to day counts as valid through its end.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The panel beside the form, <strong>Next invoice will carry</strong>, shows the result and
          a step list called <strong>How this was worked out</strong>. A profile change applies from
          the next invoice, and each saved edit is kept in the profile&rsquo;s history.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          How Revvly decides, line by line
        </h2>
        <p className="dp-plain">
          The first matching rule wins: tax off or no registration in force; the client&rsquo;s
          jurisdiction (from the profile, otherwise your primary registration&rsquo;s country); a
          valid exemption; reverse charge; no registration covering the jurisdiction; then the
          published rates in force on the invoice date that match the line&rsquo;s category, one per
          levy. Tax is rounded half-up to the currency&rsquo;s minor unit per tax line, never per
          invoice.
        </p>
        <Figure
          title="Order of the tax rules"
          caption="Revvly stops at the first rule that matches a line."
        >
          <Layers
            orderLabel="First match wins, top to bottom"
            rows={[
              {
                title: 'Tax off, or no registration in force',
                chips: ['Out of scope'],
                tone: 'muted',
              },
              {
                title: 'Find the client jurisdiction',
                note: 'From the profile, otherwise your primary registration country.',
                tone: 'muted',
              },
              { title: 'Valid exemption', chips: ['Exempt'] },
              { title: 'Reverse charge applies', chips: ['Reverse charge'] },
              {
                title: 'No registration covers the jurisdiction',
                chips: ['Out of scope'],
                tone: 'muted',
              },
              {
                title: 'Published rates in force match the line',
                note: 'One rate per levy. No matching rate means out of scope.',
                chips: ['Standard', 'Reduced', 'Zero-rated', 'Exempt'],
                tone: 'accent',
              },
            ]}
          />
        </Figure>
        <ol className="qs-steps">
          {TREATMENTS.map((t, i) => (
            <li className="qs-step" key={t.name}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{t.name}</div>
                <div className="qs-step-detail">{t.body}</div>
              </div>
            </li>
          ))}
        </ol>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Worked example</div>
            <div className="dp-field-v">
              A line of 100.00 with rate A at 5% (sequence 1) and rate B at 10% (sequence 2). If B is
              additive, tax is 5.00 and 10.00, total 15.00, gross 115.00. If B is compound, it takes
              105.00 as its base: 5.00 and 10.50, total 15.50, gross 115.50. A single 9.975% rate on
              100.00 is 9.975 before rounding and 9.98 on the invoice.
            </div>
          </div>
        </div>
        <Figure
          title="Net plus tax equals gross"
          caption="Lines stay net; each tax is added on top, so the gross is the receivable."
        >
          <Split
            total="115.00"
            parts={[
              { label: 'Net line', value: 100, amount: '100.00', tone: 'muted' },
              { label: 'Rate A tax (5%)', value: 5, amount: '5.00', tone: 'accent' },
              { label: 'Rate B tax (10%, additive)', value: 10, amount: '10.00', tone: 'warn' },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          On invoices, credit notes and receivables
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Drafts</div>
            <div className="dp-field-v">
              A held draft is stored untaxed. Tax is worked out when it moves from draft to issued,
              on the lines as they stand then. Until then the invoice page shows{' '}
              <strong>Estimated tax</strong> for the draft, at today&rsquo;s rates, and writes
              nothing. Creating an invoice by hand is taxed too; an imported invoice is a record of
              a document issued elsewhere and is left as it was.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Invoice and PDF</div>
            <div className="dp-field-v">
              Lines stay net. The totals show the subtotal, one row per tax (label, the base it
              applied to, the amount) and the gross total. Under the total the PDF prints your
              registration number, the client&rsquo;s tax ID (when Show tax IDs is on) and any
              legend.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Credit notes</div>
            <div className="dp-field-v">
              A credit note against a taxed invoice reverses the tax in proportion to the net
              credited, never more than was charged. The credit that uses up a line absorbs the
              rounding remainder. See{' '}
              <Link to="/docs/guides/credit-notes">Credit notes</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Receivables</div>
            <div className="dp-field-v">
              Receivables are gross, tax included. Once any receivable carries tax, Finance offers a{' '}
              <strong>Gross</strong>, <strong>Net</strong> and <strong>Tax only</strong> switch over
              the cards, which also show a Net and Tax line.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">08</span>
          The tax report
        </h2>
        <UiPath steps={['Finance', 'Tax']} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Pick a Period (This month, This quarter, This year or Custom From and To), Group by
          (Jurisdiction, Tax code or Month) and Basis. Totals are one per currency with no
          conversion: taxable base, tax charged, tax credited and the net tax.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Accrual (invoice date)</div>
            <div className="dp-field-v">
              Tax counts when an invoice is issued. Credit notes reduce it when they are issued.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Cash (as paid)</div>
            <div className="dp-field-v">
              Tax counts as payments arrive, in proportion to each payment and after credit notes.
              An unpaid invoice reports nothing. There is no write-off flow, so a bad debt is not
              relieved, and payments in a currency other than the invoice&rsquo;s are skipped.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Supplies without tax</div>
            <div className="dp-field-v">
              The taxable base of zero-rated, exempt, reverse-charge and out-of-scope supplies, kept
              apart because returns ask for it separately.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Drill-down</div>
            <div className="dp-field-v">
              Select a row to list the ledger rows behind it, marked Charge or Credit.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Export</div>
            <div className="dp-field-v">
              <strong>Export CSV</strong> and <strong>Export Excel</strong>, admins only. Both lead
              with your registration numbers, the period and the basis, then the grouped rows.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">09</span>
          The ledger, for your auditor
        </h2>
        <ul className="dp-connects">
          <li>
            Each taxed line writes one ledger row per tax, and credit notes write negative rows.
            Rows are append-only: they are never edited, so a later rate change cannot rewrite what
            a past invoice charged.
          </li>
          <li>
            Exempt, reverse-charge and out-of-scope lines still write a row with a tax amount of 0,
            so their taxable value is reportable. Zero-net lines write nothing.
          </li>
          <li>
            A nightly check compares each taxed invoice from the last 35 days with its ledger rows.
            An invoice with no rows at all is rebuilt from its own tax breakdown. Rows that exist
            but disagree are never rewritten; they raise an alert, and the Tax report shows a red{' '}
            <strong>mismatches</strong> badge to admins, or <strong>Reconciled · 0 mismatches</strong>{' '}
            when the ledger agrees.
          </li>
        </ul>
        <div className="api-note" style={{ marginTop: 22 }}>
          <span className="api-note-icon" aria-hidden="true">
            i
          </span>
          <div>
            Revvly applies the rates you publish. Reference rates are a starting point, and you are
            responsible for the rates you publish.
          </div>
        </div>
      </section>
    </div>
  );
}
